// Character data with taste preferences for automatic rating

export interface CustomerCharacter {
  id: string;
  name: string;
  image: string;
  color: string;
  // Flavor preferences - these affect rating
  lovedFlavors: string[];    // +bonus points
  likedFlavors: string[];    // Normal points
  dislikedFlavors: string[]; // -penalty points
  // Display text
  preferenceText: string;
  personality: string;
}

export interface ChefCharacter {
  id: string;
  name: string;
  image: string;
  color: string;
  specialty: string;
  bonusType: 'desserts' | 'speed' | 'complex';
}

export const CUSTOMER_CHARACTERS: CustomerCharacter[] = [
  { 
    id: 'emma', 
    name: 'Emma Chen', 
    image: '/images/characters/sprites/girl_1_idle.png', 
    color: '#e91e63', 
    lovedFlavors: ['Fruity', 'Sweet', 'Fresh'],
    likedFlavors: ['Creamy', 'Cold'],
    dislikedFlavors: ['Spicy', 'Savory', 'Salty'],
    preferenceText: '❤️ Loves: Fruity, Sweet',
    personality: 'cheerful',
  },
  { 
    id: 'sarah', 
    name: 'Sarah Kim', 
    image: '/images/characters/sprites/girl_2_idle.png', 
    color: '#9c27b0', 
    lovedFlavors: ['Sweet', 'Chocolate', 'Creamy'],
    likedFlavors: ['Fruity', 'Sugary', 'Buttery'],
    dislikedFlavors: ['Spicy'],
    preferenceText: '❤️ Loves: Everything sweet!',
    personality: 'enthusiastic',
  },
  { 
    id: 'margaret', 
    name: 'Margaret Thompson', 
    image: '/images/characters/sprites/girl_3_idle.png', 
    color: '#795548', 
    lovedFlavors: ['Chocolate', 'Sweet', 'Buttery'],
    likedFlavors: ['Creamy', 'Sugary'],
    dislikedFlavors: ['Spicy', 'Tangy', 'Salty'],
    preferenceText: '❤️ Loves: Chocolate, Sweet',
    personality: 'refined',
  },
  { 
    id: 'harold', 
    name: 'Harold Brown', 
    image: '/images/characters/sprites/city_man_1_idle.png', 
    color: '#ff9800', 
    lovedFlavors: ['Savory', 'Meaty', 'Hearty'],
    likedFlavors: ['Salty', 'Buttery', 'Smoky'],
    dislikedFlavors: ['Sweet', 'Fruity'],
    preferenceText: '❤️ Loves: Comfort Food',
    personality: 'practical',
  },
  { 
    id: 'robert', 
    name: 'Robert Garcia', 
    image: '/images/characters/sprites/city_man_2_idle.png', 
    color: '#4caf50', 
    lovedFlavors: ['Savory', 'Umami', 'Fresh'],
    likedFlavors: ['Tangy', 'Spicy'],
    dislikedFlavors: ['Sweet', 'Sugary', 'Chocolate'],
    preferenceText: '❤️ Loves: Savory, Umami',
    personality: 'health-conscious',
  },
  { 
    id: 'james', 
    name: 'James Wilson', 
    image: '/images/characters/sprites/city_man_3_idle.png', 
    color: '#2196f3', 
    lovedFlavors: ['Spicy', 'Savory', 'Tangy'],
    likedFlavors: ['Meaty', 'Salty', 'Umami'],
    dislikedFlavors: ['Sweet', 'Fruity', 'Creamy'],
    preferenceText: '❤️ Loves: Spicy, Savory',
    personality: 'adventurous',
  },
];

export const CHEF_CHARACTERS: ChefCharacter[] = [
  { 
    id: 'magnus', 
    name: 'Chef Magnus', 
    image: '/images/characters/sprites/chibi_wizard_idle.png', 
    color: '#673ab7', 
    specialty: 'Desserts',
    bonusType: 'desserts',
  },
  { 
    id: 'azure', 
    name: 'Chef Azure', 
    image: '/images/characters/sprites/chibi_swordsman_idle.png', 
    color: '#3f51b5', 
    specialty: 'Quick Orders',
    bonusType: 'speed',
  },
  { 
    id: 'scarlet', 
    name: 'Chef Scarlet', 
    image: '/images/characters/sprites/chibi_archer_idle.png', 
    color: '#f44336', 
    specialty: 'Complex Recipes',
    bonusType: 'complex',
  },
];

export function getCustomerCharacter(characterId: string | null): CustomerCharacter | undefined {
  return CUSTOMER_CHARACTERS.find(c => c.id === characterId);
}

export function getChefCharacter(characterId: string | null): ChefCharacter | undefined {
  return CHEF_CHARACTERS.find(c => c.id === characterId);
}

// Calculate preference match score (0-100)
export function calculatePreferenceMatch(characterId: string | null, itemFlavors: string[]): { 
  score: number; 
  matchType: 'loved' | 'liked' | 'neutral' | 'disliked';
  matchedFlavors: string[];
} {
  const character = getCustomerCharacter(characterId);
  if (!character) {
    return { score: 50, matchType: 'neutral', matchedFlavors: [] };
  }
  
  let score = 50; // Base score
  const matchedFlavors: string[] = [];
  let hasLoved = false;
  let hasDisliked = false;
  
  for (const flavor of itemFlavors) {
    if (character.lovedFlavors.includes(flavor)) {
      score += 15;
      matchedFlavors.push(`❤️ ${flavor}`);
      hasLoved = true;
    } else if (character.likedFlavors.includes(flavor)) {
      score += 8;
      matchedFlavors.push(`👍 ${flavor}`);
    } else if (character.dislikedFlavors.includes(flavor)) {
      score -= 20;
      matchedFlavors.push(`👎 ${flavor}`);
      hasDisliked = true;
    }
  }
  
  // Clamp score
  score = Math.max(0, Math.min(100, score));
  
  // Determine match type
  let matchType: 'loved' | 'liked' | 'neutral' | 'disliked';
  if (hasDisliked && !hasLoved) {
    matchType = 'disliked';
  } else if (hasLoved) {
    matchType = 'loved';
  } else if (score > 50) {
    matchType = 'liked';
  } else {
    matchType = 'neutral';
  }
  
  return { score, matchType, matchedFlavors };
}

// Get character reaction based on rating and personality
export function getCharacterReaction(characterId: string | null, rating: number): string {
  const character = getCustomerCharacter(characterId);
  const personality = character?.personality || 'cheerful';
  
  const reactions: Record<string, Record<string, string[]>> = {
    cheerful: {
      perfect: ["OMG this is AMAZING!! 🤩", "Best thing I've ever tasted!! ✨", "I'm in food heaven! 😍"],
      great: ["Yummy! This is so good! 😊", "Love it! Great job chef! 💕", "Delicious! I'm happy! 🌟"],
      good: ["Pretty tasty! Nice work! 😋", "This is good! Thanks! 👍", "Mm, I like it! 😄"],
      okay: ["It's... okay I guess? 🤔", "Hmm, not my favorite... 😅", "Could be better! 🙂"],
      poor: ["Oh no... this isn't good 😢", "I don't like this at all 😭", "What happened?? 😰"],
    },
    enthusiastic: {
      perfect: ["INCREDIBLE!! A MASTERPIECE!! 🎉", "THIS IS EVERYTHING!! 💖", "CHEF'S KISS!! 😚👨‍🍳"],
      great: ["WOW WOW WOW! So tasty!! 🤗", "Amazing work!! Love it!! 💫", "This is fantastic!! 🎊"],
      good: ["Hey, this is pretty good! 👏", "Nice job on this! 😊", "Yep, I'm enjoying this! ✌️"],
      okay: ["Hmm, it's alright... 😕", "Not super exciting... 😐", "I've had better honestly 🤷"],
      poor: ["Nooo this is bad!! 😫", "What is this?! 😵", "I'm so disappointed!! 💔"],
    },
    refined: {
      perfect: ["Exquisite. Simply divine. ✨", "A culinary masterpiece. 👏", "Perfection on a plate. 🌹"],
      great: ["Quite lovely, I must say. 😌", "Very well executed. 💐", "This meets my standards. 👌"],
      good: ["Adequate. Satisfactory. 🙂", "It's decent work. 📝", "Acceptable quality. ✔️"],
      okay: ["Rather disappointing... 😒", "Below my expectations. 📉", "This needs improvement. 🤨"],
      poor: ["Absolutely unacceptable. 😤", "This is an insult. 😠", "I demand to see the chef. 👺"],
    },
    practical: {
      perfect: ["Now THAT'S good food! 💪", "Hit the spot perfectly! 🎯", "Can't ask for better! 👍"],
      great: ["Real good! I'm satisfied! 😊", "Quality meal right here! ⭐", "Job well done! 🤝"],
      good: ["Yeah, this works. 👍", "Does the job. 🙂", "Can't complain. ✓"],
      okay: ["Eh, it's food I guess. 😐", "Not great, not terrible. 🤷", "Seen better days. 😕"],
      poor: ["This ain't it chief. 👎", "Total waste of time. 😒", "Back to basics needed. 📚"],
    },
    'health-conscious': {
      perfect: ["So fresh and delicious! 🥗", "Nourishing AND tasty! 💚", "This is perfect fuel! 🌱"],
      great: ["Really good balance! 😊", "Healthy and yummy! 🥬", "Great nutritious meal! 💪"],
      good: ["Nice and wholesome. 👍", "Good food, good vibes. 🌿", "Solid choice. ✅"],
      okay: ["Could be healthier... 🤔", "Hmm, needs more greens. 🥒", "Not the best option. 😕"],
      poor: ["This is so unhealthy! 😰", "My body says no! 🚫", "I can't eat this... 😓"],
    },
    adventurous: {
      perfect: ["BOLD flavors! LOVE IT! 🔥", "Now THIS is exciting! 🌶️", "Flavor explosion!! 💥"],
      great: ["Spicy and delicious! 😋🔥", "Great kick to it! 💪", "Adventure in my mouth! 🎢"],
      good: ["Nice flavors there. 👍", "Pretty interesting! 🤔", "I can work with this. 😊"],
      okay: ["A bit boring honestly. 😐", "Needs more punch. 👊", "Where's the excitement? 🥱"],
      poor: ["So bland and boring! 😴", "Zero flavor... 👎", "This is a snooze fest. 💤"],
    },
  };
  
  const personalityReactions = reactions[personality] || reactions.cheerful;
  let ratingKey: string;
  if (rating >= 5) ratingKey = 'perfect';
  else if (rating >= 4) ratingKey = 'great';
  else if (rating >= 3) ratingKey = 'good';
  else if (rating >= 2) ratingKey = 'okay';
  else ratingKey = 'poor';
  
  const options = personalityReactions[ratingKey];
  return options[Math.floor(Math.random() * options.length)];
}
